import React, { useState, useEffect } from 'react';
import { cashflowApi } from '../services/api';
import GenerateCashflowModal from './GenerateCashflowModal';
import SendToFinalyzerModal from './SendToFinalyzerModal';
import '../styles/Panel.css';

function CashflowPanel({ entity }) {
  const [cashflows, setCashflows] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloadingFile, setDownloadingFile] = useState(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showFinalizerModal, setShowFinalizerModal] = useState(false);
  const [selectedCashflow, setSelectedCashflow] = useState(null);

  // Load available cashflows on component mount or entity change
  useEffect(() => {
    if (entity) {
      loadCashflowsList();
    }
  }, [entity]);

  const loadCashflowsList = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await cashflowApi.listCashflows(entity.name);
      setCashflows(data.cashflows || {});
    } catch (err) {
      console.error('Error loading cashflows list:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateComplete = async (result) => {
    setShowGenerateModal(false);
    // Reload the list to show the new cashflow
    await loadCashflowsList();
  };

  const handleDownload = async (periodLabel, timestamp) => {
    setDownloadingFile(`${periodLabel}_${timestamp}`);
    try {
      const blob = await cashflowApi.downloadCashflow(entity.name, periodLabel);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Cashflow_${entity.name}_${periodLabel}_${timestamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading cashflow:', err);
      alert(`Error downloading file: ${err.message}`);
    } finally {
      setDownloadingFile(null);
    }
  };

  const handleSendToFinalyzer = (periodLabel, timestamp) => {
    setSelectedCashflow({ periodLabel, timestamp });
    setShowFinalizerModal(true);
  };

  const handleSendComplete = () => {
    setShowFinalizerModal(false);
    setSelectedCashflow(null);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      // Timestamp format: YYYYMMDD_HHMMSS
      const year = timestamp.substring(0, 4);
      const month = timestamp.substring(4, 6);
      const day = timestamp.substring(6, 8);
      const hour = timestamp.substring(9, 11);
      const minute = timestamp.substring(11, 13);
      const second = timestamp.substring(13, 15);
      return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
    } catch {
      return timestamp;
    }
  };

  const formatPeriodLabel = (periodLabel) => {
    if (!periodLabel) return 'N/A';
    // Period label format: YYYY-MM-DD_to_YYYY-MM-DD
    const parts = periodLabel.split('_to_');
    if (parts.length === 2) {
      return `${parts[0]} to ${parts[1]}`;
    }
    return periodLabel;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  // Convert cashflows object to array for rendering
  const cashflowsArray = Object.entries(cashflows).map(([periodLabel, reports]) => ({
    periodLabel,
    reports,
  }));

  return (
    <div className="panel cashflow-panel">
      <h2>Cashflow Statement - {entity.name}</h2>

      <div className="alert alert-info" style={{ marginBottom: '20px', backgroundColor: '#e3f2fd', borderColor: '#2196f3', color: '#1565c0' }}>
        <strong>About Cashflow Automation:</strong>
        <br />
        This tool automatically generates cashflow statements with detailed workings based on Trial Balance and General Ledger data from SAP.
        <br />
        <br />
        <strong>What it includes:</strong>
        <ul style={{ marginTop: '8px', marginLeft: '20px' }}>
          <li><strong>Tab 1:</strong> Trial Balance dump with Major/Minor mappings (filtered for Postable='Y')</li>
          <li><strong>Tab 2:</strong> General Ledger dump for the selected period</li>
          <li><strong>Tab 3:</strong> Period movements for BS & PnL (formula-driven)</li>
          <li><strong>Tab 4:</strong> Working 1 - FAR movements (Additions/Disposals)</li>
          <li><strong>Tab 5:</strong> Working 2 - Investment in Subsidiaries/Associates</li>
          <li><strong>Tab 6:</strong> Working 3a - Loan and Repayment - Current (Borrowings)</li>
          <li><strong>Tab 7:</strong> Working 3b - Loan and Repayment - Non-current (Borrowings)</li>
          <li><strong>Tab 8:</strong> Working 4 - Interest Paid (with optional accrual adjustment)</li>
          <li><strong>Tab 9:</strong> Finalyzer Output - Ready to send to Finalyzer</li>
        </ul>
        <br />
        <strong>Prior Period Comparison:</strong> System automatically pulls TB for the last complete fiscal year ending before Period 1 starts (e.g., if Period 1 is Apr-Dec 2025, Period 2 is Apr 2024 - Mar 2025)
        <br />
        <br />
        <strong>Requirements:</strong>
        <ul style={{ marginTop: '8px', marginLeft: '20px' }}>
          <li>Entity must have a COA mapping file configured (see <code>config/coa_mappings/</code>)</li>
          <li>Entity must be connected to SAP (EL_SAP_B1 or TL_SAP_B1)</li>
          <li>Manual upload entities are not supported</li>
        </ul>
      </div>

      <div className="section">
        <h3>Generate New Cashflow</h3>

        <button
          onClick={() => setShowGenerateModal(true)}
          className="btn btn-primary"
          style={{ marginRight: '10px' }}
        >
          Generate Cashflow
        </button>

        <button
          onClick={loadCashflowsList}
          disabled={loading}
          className="btn btn-secondary"
        >
          {loading ? 'Refreshing...' : 'Refresh List'}
        </button>
      </div>

      <div className="section" style={{ marginTop: '30px' }}>
        <h3>Available Cashflow Reports</h3>

        {loading && <div className="loading">Loading cashflows...</div>}

        {error && (
          <div className="alert alert-danger">
            <strong>Error loading cashflows:</strong> {error}
          </div>
        )}

        {!loading && !error && cashflowsArray.length === 0 && (
          <div className="empty-state">
            <p>No cashflow reports available yet. Generate your first cashflow above.</p>
          </div>
        )}

        {!loading && cashflowsArray.length > 0 && (
          <>
            {cashflowsArray.map((cashflowGroup) => (
              <div key={cashflowGroup.periodLabel} style={{ marginBottom: '40px' }}>
                <h4 style={{ color: '#1976d2', marginBottom: '15px' }}>
                  Period: {formatPeriodLabel(cashflowGroup.periodLabel)}
                </h4>

                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Generated At</th>
                        <th>File Size</th>
                        <th>Last Modified</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cashflowGroup.reports.map((report, index) => (
                        <tr key={index}>
                          <td>{formatTimestamp(report.timestamp)}</td>
                          <td>{formatFileSize(report.size_bytes)}</td>
                          <td>{new Date(report.last_modified).toLocaleString()}</td>
                          <td>
                            <button
                              onClick={() => handleDownload(cashflowGroup.periodLabel, report.timestamp)}
                              disabled={downloadingFile === `${cashflowGroup.periodLabel}_${report.timestamp}`}
                              className="btn btn-sm btn-primary"
                              style={{ marginRight: '5px' }}
                            >
                              {downloadingFile === `${cashflowGroup.periodLabel}_${report.timestamp}`
                                ? 'Downloading...'
                                : 'Download'}
                            </button>

                            <button
                              onClick={() => handleSendToFinalyzer(cashflowGroup.periodLabel, report.timestamp)}
                              className="btn btn-sm btn-success"
                            >
                              Send to Finalyzer
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Modals */}
      {showGenerateModal && (
        <GenerateCashflowModal
          entity={entity}
          onClose={() => setShowGenerateModal(false)}
          onComplete={handleGenerateComplete}
        />
      )}

      {showFinalizerModal && selectedCashflow && (
        <SendToFinalyzerModal
          entity={entity}
          periodLabel={selectedCashflow.periodLabel}
          timestamp={selectedCashflow.timestamp}
          onClose={() => {
            setShowFinalizerModal(false);
            setSelectedCashflow(null);
          }}
          onComplete={handleSendComplete}
        />
      )}
    </div>
  );
}

export default CashflowPanel;
